import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import NormalHeader from "../NormalHeader/NormalHeader";
import classes from "./NormalMode.module.scss";
import classNames from "classnames";
import { TerminalMode } from "../../../../../pagesMain/TerminalPage/TerminalPage";
import { OrderUnitsType } from "../../../../../types/Terminal/TerminalTypes";
import { useGetTicket } from "../../../../../hooks/TerminalServices/useGetTicket";
import TerminalModal from "../../../Modals/TerminalModal/TerminalModal";
import PrintedTicketModal from "../../../Modals/ModalPrintedTicket/ModalPrintedTicket";
import { isAfterPrintTicketEnabled } from "../../../../../constants/API";
import {
  TerminalAppearanceResponse,
  TerminalBackgroundMedia,
} from "../../../../../types/Admin/TerminalAppearanceTypes";
import { getMediaBackgroundSize } from "../../../../../utils/mediaBackground";
import { useTerminalAutoReturnHome } from "../../../../../hooks/TerminalServices/useTerminalAutoReturnHome";
import { useChildrenModeController } from "../../../../../hooks/TerminalServices/useChildrenModeController";
import { useChildrenModeAgeDetection } from "../../../../../hooks/TerminalServices/useChildrenModeAgeDetection";
import {
  createChildrenTileColorMap,
  resolveChildrenModeVisualSettings,
} from "../../../../../utils/Terminal/childrenModeThemes";
import {
  ChildrenMode,
} from "../../ChildrenMode/ChildrenMode";
import { NormalModeView } from "./NormalModeView";
import {
  buildChildrenAppearanceStyle,
  buildNormalAppearanceStyle,
  checkIsCategory,
  elementsLocalization,
  getMediaObjectFit,
  getVideoMimeType,
  languageType,
  resolveChildrenBackground,
  resolveNormalBackground,
  TerminalTicketItem,
  transformOrderUnitsToArr,
} from "./terminalModeShared";

export type { languageType };

type NormalModePropsType = {
  setMode: (mode: TerminalMode) => void;
  orderUnitsData: OrderUnitsType;
  tickerText?: string;
  appearance?: TerminalAppearanceResponse;
  previewChildrenModeOverride?: boolean;
  isPreviewMode?: boolean;
};

export const NormalMode: FC<NormalModePropsType> = ({
  setMode,
  tickerText,
  orderUnitsData,
  appearance,
  previewChildrenModeOverride,
  isPreviewMode = false,
}) => {
  const [language, setLanguage] = useState<languageType>("ru");
  const [currentSectionsServices, setCurrentSectionsServices] = useState<
    TerminalTicketItem[]
  >([]);
  const [history, setHistory] = useState<{
    [key: string]: TerminalTicketItem[];
  }>({});
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [activeModal, setActiveModal] = useState(false);
  const [activeTicketId, setActiveTicketId] = useState<number>();
  const [childrenTileColors, setChildrenTileColors] = useState<
    Map<string, string>
  >(new Map());

  const childrenModeSettings = appearance?.settings?.childrenMode;
  const childrenVisual = useMemo(
    () => resolveChildrenModeVisualSettings(childrenModeSettings),
    [
      childrenModeSettings?.hideIcons,
      childrenModeSettings?.showStandardModeIcons,
      childrenModeSettings?.decorationType,
      childrenModeSettings?.presetThemeId,
      childrenModeSettings?.buttonShape,
      childrenModeSettings?.backgroundFitMode,
      childrenModeSettings?.backgroundOffsetByHeader,
      childrenModeSettings?.activeBackgroundId,
    ],
  );
  const presetThemeId = childrenModeSettings?.presetThemeId;

  const endSessionChildrenModeRef = useRef<() => void>(() => {});
  const orderUnitsFingerprintRef = useRef("");
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  const { getTicket, isLoadingGetTicket } = useGetTicket(() => {
    if (currentIndex > 0) {
      setCurrentIndex(0);
      setCurrentSectionsServices(history[0]);
    }
    endSessionChildrenModeRef.current();
  }, orderUnitsData?.terminalIp);

  const {
    isChildrenMode: controllerChildrenMode,
    showChildrenModeButton,
    toggleSessionChildrenMode,
    enableSessionChildrenMode,
    endSessionChildrenMode,
    switchByAgeEnabled,
    ageThreshold,
  } = useChildrenModeController({
    childrenModeSettings,
    currentIndex,
    activeModal,
    isLoadingGetTicket,
  });

  endSessionChildrenModeRef.current = endSessionChildrenMode;

  const isChildrenMode =
    typeof previewChildrenModeOverride === "boolean"
      ? previewChildrenModeOverride
      : controllerChildrenMode;

  const handleChildrenModeDetected = useCallback(
    (value: boolean) => {
      if (value) {
        enableSessionChildrenMode();
      }
    },
    [enableSessionChildrenMode],
  );

  const { videoRef } = useChildrenModeAgeDetection({
    enabled: switchByAgeEnabled && !isPreviewMode,
    ageThreshold,
    isChildrenMode,
    setIsChildrenMode: handleChildrenModeDetected,
    isPreviewMode,
  });

  useEffect(() => {
    if (!isChildrenMode) {
      setChildrenTileColors(new Map());
      return;
    }
    setChildrenTileColors(
      createChildrenTileColorMap(
        currentSectionsServices.map((ticket) => ticket.id),
      ),
    );
  }, [isChildrenMode, currentSectionsServices]);

  useEffect(() => {
    setActiveModal(isLoadingGetTicket);
  }, [isLoadingGetTicket]);

  useEffect(() => {
    if (!orderUnitsData) {
      return;
    }

    const fingerprint = JSON.stringify(orderUnitsData);
    if (fingerprint === orderUnitsFingerprintRef.current) {
      return;
    }
    orderUnitsFingerprintRef.current = fingerprint;

    setCurrentSectionsServices(transformOrderUnitsToArr(orderUnitsData));

    if (currentIndexRef.current > 0) {
      setCurrentIndex(0);
    }
  }, [orderUnitsData]);

  const handleCategoryClick = (item: TerminalTicketItem) => {
    if (checkIsCategory(item)) {
      setHistory((prev) => {
        const newHistory = { ...prev };
        newHistory[currentIndex] = currentSectionsServices;
        return newHistory;
      });
      setCurrentSectionsServices([
        ...(item.topics || []),
        ...(item.subCategories || []),
      ]);
      setCurrentIndex((prev) => prev + 1);
    } else {
      setActiveTicketId(item.id);
      getTicket({ topicId: item.id });
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setCurrentSectionsServices(history[currentIndex - 1]);
    }
  };

  const autoReturnHomeIdleSeconds =
    appearance?.settings?.autoReturnHome?.idleSeconds ?? 0;
  const isOnHomePage = currentIndex === 0 && !isChildrenMode && !activeModal;

  const returnToHome = useCallback(() => {
    if (orderUnitsData) {
      setCurrentIndex(0);
      setHistory({});
      setCurrentSectionsServices(transformOrderUnitsToArr(orderUnitsData));
    }
    endSessionChildrenMode();
  }, [orderUnitsData, endSessionChildrenMode]);

  useTerminalAutoReturnHome({
    idleSeconds: autoReturnHomeIdleSeconds,
    isOnHomePage,
    onReturnHome: returnToHome,
    enabled: !isPreviewMode,
  });

  const activeBackground = useMemo<TerminalBackgroundMedia | null>(() => {
    if (isChildrenMode) {
      return resolveChildrenBackground(appearance, childrenVisual);
    }
    return resolveNormalBackground(appearance);
  }, [appearance, isChildrenMode, childrenVisual]);

  const greetingTitle =
    appearance?.settings?.greeting?.title?.[language] ||
    elementsLocalization.title[language];
  const greetingSubTitle =
    appearance?.settings?.greeting?.subTitle?.[language] ||
    elementsLocalization.subTitle[language];
  const isGreetingEnabled = appearance?.settings?.greeting?.isEnabled ?? true;
  const showGreeting = isGreetingEnabled;

  const appearanceStyle = useMemo(() => {
    if (isChildrenMode) {
      return buildChildrenAppearanceStyle(appearance, childrenVisual);
    }
    return buildNormalAppearanceStyle(appearance);
  }, [appearance, isChildrenMode, childrenVisual]);

  const useCategoryGrid = useMemo(() => {
    if (currentSectionsServices.length === 0) return false;
    const hasCategory = currentSectionsServices.some((item) =>
      checkIsCategory(item),
    );
    const hasService = currentSectionsServices.some(
      (item) => !checkIsCategory(item),
    );
    return hasCategory && !hasService;
  }, [currentSectionsServices]);

  const hasBackground = Boolean(activeBackground);
  const isBackgroundOffsetByHeader = isChildrenMode
    ? childrenVisual.backgroundOffsetByHeader
    : (appearance?.settings?.backgroundOffsetByHeader ?? false);
  const backgroundFitMode = isChildrenMode
    ? childrenVisual.backgroundFitMode
    : (appearance?.settings?.backgroundFitMode ?? "cover");

  const hideChildrenModeToggle =
    typeof previewChildrenModeOverride === "boolean";

  return (
    <div
      className={classNames(classes.normalModeContainer, {
        [classes.withBackground]: hasBackground,
        [classes.childrenMode]: isChildrenMode,
        [classes.childrenModePlainBackground]: isChildrenMode && !hasBackground,
      })}
      style={{
        ...appearanceStyle,
        ...(isChildrenMode && !hasBackground
          ? { backgroundColor: childrenVisual.terminalBackgroundColor }
          : {}),
      }}
    >
      {activeBackground?.mediaType === "IMAGE" && (
        <div
          className={classNames(classes.backgroundLayer, {
            [classes.backgroundOffsetHeader]: isBackgroundOffsetByHeader,
          })}
          style={{
            backgroundImage: `url(${activeBackground.url})`,
            backgroundSize: getMediaBackgroundSize(backgroundFitMode, 100),
          }}
        />
      )}
      {activeBackground?.mediaType === "VIDEO" && (
        <video
          className={classNames(classes.backgroundVideo, {
            [classes.backgroundOffsetHeader]: isBackgroundOffsetByHeader,
          })}
          style={{ objectFit: getMediaObjectFit(backgroundFitMode) }}
          autoPlay
          loop
          muted
          playsInline
        >
          <source
            src={activeBackground.url}
            type={getVideoMimeType(activeBackground.url)}
          />
        </video>
      )}
      {activeModal && (
        <TerminalModal
          activeModal={activeModal}
          setActiveModal={setActiveModal}
        />
      )}
      <div className={classes.contentLayer}>
        <div className={classes.headerContainer}>
          <NormalHeader
            language={language}
            setLanguage={setLanguage}
            setMode={setMode}
            appearance={appearance}
          />
        </div>
        <main className={classes.normalModeMain}>
          {isAfterPrintTicketEnabled && (
            <PrintedTicketModal
              activeModal={true}
              setActiveModal={setActiveModal}
            />
          )}

          {!isChildrenMode && (
            <NormalModeView
              language={language}
              currentSectionsServices={currentSectionsServices}
              currentIndex={currentIndex}
              activeModal={activeModal}
              isLoadingGetTicket={isLoadingGetTicket}
              activeTicketId={activeTicketId}
              appearance={appearance}
              useCategoryGrid={useCategoryGrid}
              showChildrenModeButton={showChildrenModeButton}
              hideChildrenModeToggle={hideChildrenModeToggle}
              showGreeting={showGreeting}
              greetingTitle={greetingTitle}
              greetingSubTitle={greetingSubTitle}
              onBack={handleBack}
              onToggleChildrenMode={toggleSessionChildrenMode}
              onCategoryClick={handleCategoryClick}
            />
          )}

          {isChildrenMode && (
            <ChildrenMode
              language={language}
              currentSectionsServices={currentSectionsServices}
              currentIndex={currentIndex}
              activeModal={activeModal}
              isLoadingGetTicket={isLoadingGetTicket}
              activeTicketId={activeTicketId}
              appearance={appearance}
              childrenVisual={childrenVisual}
              presetThemeId={presetThemeId}
              childrenTileColors={childrenTileColors}
              useCategoryGrid={useCategoryGrid}
              showChildrenModeButton={showChildrenModeButton}
              hideChildrenModeToggle={hideChildrenModeToggle}
              showGreeting={showGreeting}
              greetingTitle={greetingTitle}
              greetingSubTitle={greetingSubTitle}
              onBack={handleBack}
              onToggleChildrenMode={toggleSessionChildrenMode}
              onCategoryClick={handleCategoryClick}
            />
          )}
        </main>
        <footer>
          <div className={classes.normalModeFooter}>
            {tickerText && (
              <div className={classes.tickerMarquee}>
                <div className={classes.tickerText}>{tickerText}</div>
                <div className={classes.tickerText} aria-hidden="true">
                  {tickerText}
                </div>
              </div>
            )}
          </div>
        </footer>
      </div>
      {switchByAgeEnabled && !isPreviewMode && (
        <video ref={videoRef} style={{ display: "none" }} muted playsInline />
      )}
    </div>
  );
};

export default NormalMode;
